import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * The Release 1 dashboards (FRD-RPT-002; BRD §12, §25): CEO, Finance, HR, Procurement and
 * Marketing, and the COO dashboard (CLR-47). Each is its own record type, `reporting.<code>_dashboard`, with `view` and `export`
 * (FRD-RPT-008, RPT-012).
 */
export const DASHBOARD_CODES = ['ceo', 'coo', 'finance', 'hr', 'procurement', 'marketing'];
export const DashboardCodeSchema = z.enum(DASHBOARD_CODES);
/** A dashboard the caller may open. */
export const DashboardSummarySchema = z.object({
    code: DashboardCodeSchema,
    title: z.string(),
    recordType: z.string(),
});
/**
 * The dimensions a dashboard may be filtered by (FRD-RPT-010): the organizational ones and the
 * supplier, employee, client and asset the records carry. Each key is the same in the dashboard
 * URL, the API query and the drill path. A measure is filtered only when every list it is drawn
 * from carries the dimension; otherwise it says it is not filterable by it, and never shows the
 * unfiltered figure in its place. The account dimension belongs to the Finance measures.
 */
export const DASHBOARD_DIMENSIONS = [
    'department_id',
    'cost_center_id',
    'project_id',
    'location_id',
    'supplier_id',
    'employee_id',
    'client_id',
    'asset_id',
];
export const DashboardDimensionSchema = z.enum(DASHBOARD_DIMENSIONS);
/** The period a dashboard presents (FRD-RPT-010, RPT-011): business dates, inclusive. */
export const DashboardQuerySchema = z.strictObject({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    department_id: IdSchema.optional(),
    cost_center_id: IdSchema.optional(),
    project_id: IdSchema.optional(),
    location_id: IdSchema.optional(),
    supplier_id: IdSchema.optional(),
    employee_id: IdSchema.optional(),
    client_id: IdSchema.optional(),
    asset_id: IdSchema.optional(),
});
export const MEASURE_UNITS = ['count', 'AED', 'days', 'percent'];
export const MeasureUnitSchema = z.enum(MEASURE_UNITS);
/** One line of a measure's breakdown, with the filtered list behind it when there is one. */
export const MeasureBreakdownSchema = z.object({
    label: z.string(),
    value: z.string(),
    drillPath: z.string().nullable(),
});
/**
 * Why a measure carries no value: its capability is not delivered (FRD-RPT-013), the caller may
 * not see its source records (FRD-RPT-008), it awaits configuration, its source cannot be filtered
 * by a chosen dimension (FRD-RPT-010), or it could not be calculated this time.
 */
export const MEASURE_UNAVAILABLE_KINDS = [
    'not_delivered',
    'not_permitted',
    'not_configured',
    'not_filterable',
    'failed',
];
export const MeasureUnavailableKindSchema = z.enum(MEASURE_UNAVAILABLE_KINDS);
/**
 * One measure (FRD-RPT-004, RPT-007, RPT-011, RPT-013). `value` is a decimal string: a count, an
 * AED amount at 2 decimals, or days. An unavailable measure carries no value, only the reason —
 * never a zero standing in for a figure that cannot be given.
 */
export const DashboardMeasureSchema = z.object({
    code: z.string(),
    label: z.string(),
    available: z.boolean(),
    reason: z.string().optional(),
    value: z.string().optional(),
    unit: MeasureUnitSchema,
    basis: z.string(),
    breakdown: z.array(MeasureBreakdownSchema).optional(),
    drillPath: z.string().optional(),
    unavailable: MeasureUnavailableKindSchema.optional(),
    /** The request's correlation id, when the measure could not be calculated. */
    correlationId: z.string().optional(),
    /**
     * True when the caller's grant on a source record type is limited to part of the organisation
     * (FRD-USR-007, RPT-008): the figure counts only the records within that scope.
     */
    scopeLimited: z.boolean().optional(),
    /** How a breakdown is drawn: bars (the default) or a pie of shares (CLR-47). */
    chart: z.enum(['bars', 'pie']).optional(),
});
export const DashboardSchema = z.object({
    code: DashboardCodeSchema,
    title: z.string(),
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    /** The dimension filters applied (FRD-RPT-010). */
    filters: z.partialRecord(DashboardDimensionSchema, IdSchema).optional(),
    measures: z.array(DashboardMeasureSchema),
});
//# sourceMappingURL=reporting.js.map